import { useEffect, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import clsx from "clsx";
import { motion, AnimatePresence } from "framer-motion";

type Tone = "default" | "danger" | "success" | "warning";

interface FgDialogProps {
  open: boolean;
  title: string;
  description?: string;
  tone?: Tone;
  size?: "sm" | "md" | "lg";
  children?: ReactNode;
  footer?: ReactNode;
  onClose: () => void;
  /**
   * If true, the dialog will confirm with the browser before allowing the
   * user to close (X button, ESC key, backdrop click). Use when the
   * dialog has unsaved user input. The browser-native `confirm()` is
   * intentionally kept simple — the admin portal has no a11y-friendly
   * modal-of-modals infrastructure.
   */
  confirmOnClose?: boolean;
  /** Message shown in the confirm prompt. */
  confirmMessage?: string;
}

const SIZE = {
  sm: "max-w-[400px]",
  md: "max-w-[560px]",
  lg: "max-w-[720px]",
} as const;

const TONE_ACCENT: Record<Tone, string> = {
  default: "bg-[#FFD60A] text-[#0A0A0A]",
  danger: "bg-[#E63946] text-white",
  success: "bg-[#34C759] text-[#0A0A0A]",
  warning: "bg-[#FF9F0A] text-[#0A0A0A]",
};

/**
 * Dark-theme modal — backdrop blur, scaled entrance, escape-to-close,
 * focus-trap entry. Matches the spec's `Modal` feel while keeping the
 * `FgDialog` API.
 */
export function FgDialog({
  open,
  title,
  description,
  tone = "default",
  size = "md",
  children,
  footer,
  onClose,
  confirmOnClose,
  confirmMessage,
}: FgDialogProps) {
  const dialogRef = useRef<HTMLDivElement>(null);

  const handleClose = () => {
    if (confirmOnClose) {
      const msg = confirmMessage ?? "Discard unsaved changes?";
      // eslint-disable-next-line no-alert
      if (!window.confirm(msg)) return;
    }
    onClose();
  };

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") handleClose();
    };
    document.addEventListener("keydown", onKey);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = previousOverflow;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, onClose, confirmOnClose]);

  useEffect(() => {
    if (open && dialogRef.current) {
      const focusable = dialogRef.current.querySelector<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      focusable?.focus();
    }
  }, [open]);

  // Portal to document.body so the modal escapes the AdminShell wrapper's
  // `motion.div` (which carries a `transform`, creating a containing block
  // for any `position: fixed` descendant and anchoring the modal to the
  // top of the page instead of the viewport).
  if (typeof document === "undefined") return null;

  return createPortal(
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={handleClose}
            className="absolute inset-0 bg-black/60 backdrop-blur-md"
          />
          <motion.div
            ref={dialogRef}
            initial={{ opacity: 0, scale: 0.92, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 8, transition: { duration: 0.15 } }}
            transition={{ type: "spring", stiffness: 400, damping: 30 }}
            role="dialog"
            aria-modal="true"
            aria-label={title}
            className={clsx(
              "relative w-full bg-[rgba(28,28,30,0.98)] backdrop-blur-2xl border border-[rgba(255,255,255,0.1)] rounded-2xl shadow-[0_24px_80px_rgba(0,0,0,0.7)] overflow-hidden flex flex-col max-h-[90vh] z-10",
              SIZE[size],
            )}
            onClick={(e) => e.stopPropagation()}
          >
            <header className="sticky top-0 z-20 flex items-center justify-between px-5 py-3.5 border-b border-[rgba(255,255,255,0.08)] bg-[rgba(20,20,20,0.6)] backdrop-blur-md">
              <div className="flex items-center gap-3">
                <span
                  className={clsx(
                    "flex h-8 w-8 items-center justify-center rounded-md text-sm font-bold",
                    TONE_ACCENT[tone],
                  )}
                >
                  !
                </span>
                <h2 className="text-base font-bold text-[#FAFAFA]">{title}</h2>
              </div>
              <button
                type="button"
                aria-label="Close dialog"
                onClick={handleClose}
                className="p-1.5 rounded-lg text-[#8E8E93] hover:text-[#FAFAFA] hover:bg-white/10 transition-colors"
              >
                <X size={18} />
              </button>
            </header>
            {description && (
              <p className="px-5 pt-3 text-sm text-[#8E8E93]">{description}</p>
            )}
            <div className="px-5 py-4 text-sm text-[#FAFAFA]/90 overflow-y-auto">{children}</div>
            {footer && (
              <footer className="sticky bottom-0 z-20 flex items-center justify-end gap-2.5 px-5 py-3 border-t border-[rgba(255,255,255,0.08)] bg-[rgba(20,20,20,0.6)] backdrop-blur-md">
                {footer}
              </footer>
            )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body,
  );
}
